import { TypeDefinitionAttribute } from '../../codegen/graphql';

import { ReactNode } from 'react';
import { FormikHelpersRef } from '../../types';

export interface TDAStepperFormProps {
    attributes: TypeDefinitionAttribute[];
    onSubmit: (mergedValues: Record<string, any>) => void | Promise<void>;
    onCancel?: () => void;
    initialFormValues?: Record<string, any>;
    /**
     * Values merged live UNDER each step's cached values (unlike `initialFormValues`,
     * which is snapshotted per step once at mount). Use for keys that must stay in a
     * step's Formik initial values even when the step (or the key's attribute) only
     * appears after mount — e.g. seeds for conditionally hidden attributes, where a
     * changing key set would otherwise reset the step's form. Must be referentially
     * stable per render (memoized) and value-stable while the user types: it feeds
     * Formik's `enableReinitialize` deep-compare on every render.
     */
    stepSeedValues?: Record<string, any>;
    finishButtonText?: string;
    defaultCategoryLabel?: string;
    categoryLabels?: Record<string, string>;
    isSubmitting?: boolean;
    onStepValuesChange?: (categoryKey: string, values: Record<string, any>) => void;
    hideStepper?: boolean;
    /**
     * Optional content rendered above the form body in place of the default WizardStepper.
     * When provided, the stepper is not rendered regardless of `hideStepper`.
     */
    headerContent?: ReactNode;
    customInputRenderers?: Record<string, any>;
    /**
     * Called before Formik validates on Next-click. Lets the parent run a side
     * effect that must succeed before advance (e.g. trigger an embedded form's
     * submit so the synthetic field value gets populated). Throw / reject to
     * block advance.
     */
    onBeforeNext?: (stepKey: string) => Promise<void>;
    /**
     * Step index the wizard starts on (default 0). Combined with an external
     * `key`, this lets the parent remount the stepper directly onto a later step
     * (e.g. after the install-event location is confirmed and node-scoped TDAs
     * have been loaded). `highestStepReached` is seeded from this too, so Back is
     * available to earlier steps.
     */
    initialActiveStep?: number;
    /**
     * Total displayed in the progress bar while the real step count is not yet
     * known (e.g. before the install-event location picker has loaded its
     * node-scoped TDAs). Used only while `stepsFinalized` is false.
     */
    expectedTotalSteps?: number;
    /**
     * When false, the progress total falls back to `max(steps.length, expectedTotalSteps)`
     * so the bar shows an expected count instead of the transient real one. When
     * true (default), the real `steps.length` is used.
     */
    stepsFinalized?: boolean;
    /** Renders a spinner on the Next button (e.g. while a node-scoped TDA load is in flight). */
    isNextLoading?: boolean;
    /**
     * Receives the FormikHelpersRef of the currently mounted step form (re-fires on
     * every step mount). Lets the parent live-write into the mounted step via
     * setFieldValue — e.g. the move wizard's removalDate→runningTime sync.
     */
    onFormikHelpersRef?: (ref: FormikHelpersRef) => void;
    /** Scopes the steps' installedBaseNode pickers/browsers to the subtree below
     *  this node (see InstalledBaseTDAForm). Undefined = unrestricted. */
    nodePickerRootNodeId?: string;
}

export const NO_CATEGORY_KEY = '__no_category__';
